// @ts-nocheck
import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { v4 as uuidv4 } from 'uuid';
import { createOrder } from '../../store/slices/orders';
import { getAffiliateProduct } from '../../store/slices/products';
import { getPriceFrequency, renderPriceFrequency, constructPrice } from '../../utils/helpers';
import api from '../../api';
import { resolveInitialTargetOrganisationId, shouldDisableCreateOrder } from './createOrderDialog.helpers';
import ProductOptionsSelect from './ProductOptionsSelect';
import { useAccessProductOptions } from './hooks/useAccessProductOptions';

const useStyles = makeStyles(() => ({
    formControl: {
        marginTop: 20,
        minWidth: 120,
    },
}));

const CreateOrderDialog = ({ open, onClose, onAccept, isLaw, customerInput }) => {
    const styles = useStyles();
    const dispatch = useDispatch();
    const { organisationId } = useParams();
    const navigate = useNavigate();
    const user = useSelector(state => state.auth.user);

    const [availableOrganisations, setAvailableOrganisations] = useState([]);
    const [organisationsLoading, setOrganisationsLoading] = useState(false);
    const [organisationsError, setOrganisationsError] = useState('');
    const [targetOrganisationId, setTargetOrganisationId] = useState(organisationId);

    const [selectedProducts, setSelectedProducts] = useState([]);
    const [customer, setCustomer] = useState(customerInput ?? { firstName: '', lastName: '', email: '' });
    const [error, setError] = useState({ email: '', products: '' });
    const affiliateId = isLaw ? 'jura-nord-advokater-aps' : 'penly';

    useEffect(() => {
        if (!targetOrganisationId) return;
        dispatch(getAffiliateProduct(targetOrganisationId, affiliateId));
    }, [dispatch, affiliateId, targetOrganisationId]);

    useEffect(() => {
        if (!open) return;

        let isMounted = true;

        const loadOrganisations = async () => {
            setOrganisationsLoading(true);
            setOrganisationsError('');

            try {
                const organisations = await api.teal.getOrderOrganisations();
                if (!isMounted) return;

                setAvailableOrganisations(organisations || []);

                setTargetOrganisationId((currentTargetOrganisationId) => resolveInitialTargetOrganisationId({
                    organisationId,
                    availableOrganisations: organisations,
                    currentTargetOrganisationId,
                }));
            } catch (e) {
                if (!isMounted) return;
                setOrganisationsError('Kunne ikke hente organisationer. Prøv igen.');
                setAvailableOrganisations([]);
                setTargetOrganisationId(undefined);
            } finally {
                if (isMounted) {
                    setOrganisationsLoading(false);
                }
            }
        };

        loadOrganisations();

        return () => {
            isMounted = false;
        };
    }, [open, organisationId]);
    const { lawProductOptions, nonLawProductOptions, productsById } = useAccessProductOptions(organisationId, isLaw);
    const resetState = () => {
        setSelectedProducts([]);
        setCustomer({ firstName: '', lastName: '', email: '' });
        setError({ email: '', products: '' });
    };

    const handleProductChange = (event) => {
        const selectedIds = event.target.value;
        const updatedProducts = selectedIds.map(id => {
            const existingProduct = selectedProducts.find(product => product.product.id === id);
            if (existingProduct) {
                return existingProduct;
            }
            const product = productsById[id] || {
                id,
                name: [...lawProductOptions, ...nonLawProductOptions].find((option) => option.value === id)?.name || id,
                price: {},
            };
            const defaultPrice = product.price.hourly
                ? product.price.standard
                : product.price.now || product.price.standard || product.price.yearly?.now || product.price.yearly?.standard || product.price.monthly?.standard;
            const priceFrequency = getPriceFrequency(product.price);
            return { product, price: defaultPrice || 0, priceFrequency };
        });
        setSelectedProducts(updatedProducts);
    };

    const handlePriceChange = (index) => (event) => {
        const updatedProducts = [...selectedProducts];
        updatedProducts[index].price = parseFloat(event.target.value) || 0;
        setSelectedProducts(updatedProducts);
    };

    const handleCustomerChange = (field) => (event) => {
        setCustomer({ ...customer, [field]: event.target.value });
    };

    const handleTargetOrganisationChange = (event) => {
        setTargetOrganisationId(event.target.value);
        setSelectedProducts([]);
        setError((prev) => ({ ...prev, products: '' }));
    };

    const handleAccept = async () => {
        let hasError = false;
        const newError = { email: '', products: '' };

        if (!customer.email) {
            newError.email = 'Email skal udfyldes.';
            hasError = true;
        }

        if (selectedProducts.length === 0) {
            newError.products = 'Du skal vælge mindst ét produkt.';
            hasError = true;
        }

        setError(newError);

        if (!hasError) {
            const orderToCreate = {
                items: selectedProducts.map(({ product, price, priceFrequency }) => ({
                    product: {
                        ...product,
                        price: constructPrice(price, priceFrequency),
                    },
                })),
                customer,
                createdBy: { userId: user.userId, name: user.fullName },
                vendor: targetOrganisationId,
                targetOrganisationId,
                calendlyBookingId: uuidv4(),
                disableCalendlyLookup: true,
            };
            const res = await dispatch(createOrder(organisationId, orderToCreate));
            if (res.isError) {
                // eslint-disable-next-line no-alert
                alert('Kunne ikke oprette ordre');
            } else {
                if (onAccept) {
                    onAccept();
                }
                navigate(`/${targetOrganisationId}/access/orders/${res.orderId}`);
                resetState();
            }
        }
    };

    return (
        <Dialog
            open={ open }
            onClose={ onClose }
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{ sx: { width: '660px', maxWidth: '96vw' } }}
        >
            <DialogTitle id="alert-dialog-title">Opret ny ordre</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">Vælg produkter, rediger priser og indtast kundeoplysninger for den nye ordre</DialogContentText>

                <FormControl className={ styles.formControl } fullWidth error={ !!organisationsError }>
                    <InputLabel id="organisation-select-label">Organisation</InputLabel>
                    <Select
                        labelId="organisation-select-label"
                        value={ targetOrganisationId || '' }
                        onChange={ handleTargetOrganisationChange }
                        input={ <OutlinedInput label="Organisation" /> }
                        disabled={ organisationsLoading || availableOrganisations.length === 0 }
                    >
                        { availableOrganisations.map(({ organisationId, name }) => (
                            <MenuItem key={ organisationId } value={ organisationId }>{ name }</MenuItem>
                        )) }
                    </Select>
                    { organisationsLoading && <Typography variant="caption">Henter organisationer...</Typography> }
                    { organisationsError && <Typography color="error" variant="caption">{ organisationsError }</Typography> }
                </FormControl>

                <Card variant="outlined" sx={{ marginBottom: 2, marginTop: 2, borderColor: '#009094' }}>
                    <CardContent sx={{ paddingBottom: '12px !important' }}>
                        <Typography gutterBottom sx={{ color: '#009094', fontSize: 12, margin:0 }}>
                            Kunde
                        </Typography>
                        <Grid container spacing={ 2 }>
                            <Grid item xs={ 6 }>
                                <TextField
                                    label="Fornavn"
                                    value={ customer.firstName }
                                    onChange={ handleCustomerChange('firstName') }
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={ 6 }>
                                <TextField
                                    label="Efternavn"
                                    value={ customer.lastName }
                                    onChange={ handleCustomerChange('lastName') }
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            label="Email"
                            value={ customer.email }
                            onChange={ handleCustomerChange('email') }
                            fullWidth
                            margin="normal"
                            error={ !!error.email }
                            helperText={ error.email }
                        />
                    </CardContent>
                </Card>
                <div className={ styles.formControl }>
                    <ProductOptionsSelect
                        labelId="multi-select-label"
                        label="Vælg produkter"
                        multiple
                        value={ selectedProducts.map(({ product }) => product.id) }
                        onChange={ handleProductChange }
                        lawProductOptions={ lawProductOptions }
                        nonLawProductOptions={ nonLawProductOptions }
                        menuProps={{ anchorOrigin: { vertical: 'top', horizontal: 'left' }, transformOrigin: { vertical: 'bottom', horizontal: 'left' } }}
                        fullWidth
                        error={ !!error.products }
                    />
                    { error.products && <Typography color="error" variant="caption">{ error.products }</Typography> }
                </div>
                { selectedProducts.map((item, index) => (
                    <Grid container spacing={ 2 } key={ index } alignItems="center">
                        <Grid item xs={ 4 }>
                            <TextField
                                label="Produkt"
                                value={ item.product.name }
                                disabled
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={ 4 }>
                            <TextField
                                label="Pris"
                                type="number"
                                value={ item.price }
                                onChange={ handlePriceChange(index) }
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={ 4 }>
                            <TextField
                                label="Pris frekvens"
                                value={ renderPriceFrequency(item.priceFrequency) }
                                disabled
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                    </Grid>
                )) }
            </DialogContent>
            <DialogActions>
                <Button onClick={ onClose } color="warning">
                    Fortryd
                </Button>
                <Button
                    onClick={ handleAccept }
                    autoFocus
                    color="primary"
                    variant="contained"
                    disabled={ shouldDisableCreateOrder({ organisationsLoading, organisationsError, targetOrganisationId }) }
                >
                    Opret
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateOrderDialog;
